"use client";

import { useId, useState } from "react";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import { FormattedMessage } from "react-intl";
import { FaMinus, FaPlus } from "react-icons/fa6";
import type { FaqItem } from "@/lib/ressources";
import { DEMO_URL } from "@/lib/demo";
import { homePath } from "@/lib/locale-routing";
import { trackCtaClick } from "@/lib/analytics/track";

// Same accordion as the landing FAQSection (Framer Motion height/opacity
// animation) so open/close feels identical everywhere. The answer text
// missing from the closed-state DOM is fine for SEO: the article page
// already emits a FAQPage JSON-LD block with every question/answer.
function FaqAccordionItem({ item, open, onToggle }: { item: FaqItem; open: boolean; onToggle: () => void }) {
  // Generated ids: the question text (spaces, quotes) is not a valid id reference.
  const uid = useId();
  const panelId = `article-faq-panel-${uid}`;
  const buttonId = `article-faq-button-${uid}`;

  return (
    <div
      className={`rounded-2xl bg-surface border transition-colors duration-200 ${
        open ? "border-blue-main" : "border-transparent"
      }`}
    >
      <h3>
        <button
          id={buttonId}
          type="button"
          aria-expanded={open}
          // The panel only exists while open (it animates in and out of the DOM).
          aria-controls={open ? panelId : undefined}
          onClick={onToggle}
          className="w-full flex items-start justify-between gap-4 text-left px-5 sm:px-6 py-4 sm:py-5 cursor-pointer"
        >
          <span className="text-sm sm:text-base font-bold text-text-main 2xl:text-lg">{item.question}</span>
          <span
            aria-hidden="true"
            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors duration-200 ${
              open ? "bg-blue-main text-white" : "bg-blue-ulight text-brand"
            }`}
          >
            {open ? <FaMinus className="text-[0.625rem]" /> : <FaPlus className="text-[0.625rem]" />}
          </span>
        </button>
      </h3>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={panelId}
            role="region"
            aria-labelledby={buttonId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="px-5 sm:px-6 pb-5 text-sm text-gray-main leading-relaxed 2xl:text-base">{item.answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * The article's FAQ block: an accordion (one answer open at a time) and the « still
 * stuck? » card linking to the home contact form of the article's language and to a demo.
 *
 * @param items - Questions and answers from the article front matter.
 * @param locale - Article language (the contact link goes to the matching home page).
 */
export function ArticleFaq({ items, locale = "fr" }: { items: FaqItem[] | undefined; locale?: "fr" | "en" }) {
  const [openQuestion, setOpenQuestion] = useState<string | null>(null);

  if (!items || items.length === 0) return null;

  const handleToggle = (question: string) => {
    setOpenQuestion((current) => (current === question ? null : question));
  };

  return (
    <section aria-labelledby="article-faq-title" className="w-full py-20 px-4 bg-gray-ulight">
      <div className="max-w-3xl mx-auto 2xl:max-w-4xl">
        <div className="text-center flex flex-col items-center">
          <span className="inline-block bg-blue-ulight text-brand-ink rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider">
            <FormattedMessage id="faq.eyebrow" />
          </span>
          <h2 id="article-faq-title" className="mt-5 text-2xl sm:text-3xl lg:text-4xl font-bold text-text-main text-balance">
            <FormattedMessage id="ressources.faq.title" />
          </h2>
        </div>

        <div className="mt-10 flex flex-col gap-3">
          {items.map((item) => (
            <FaqAccordionItem
              key={item.question}
              item={item}
              open={openQuestion === item.question}
              onToggle={() => handleToggle(item.question)}
            />
          ))}
        </div>

        <div className="mt-6 rounded-2xl bg-surface px-5 sm:px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <div className="flex-1">
            <p className="text-sm font-bold text-text-main">
              <FormattedMessage id="faq.more.title" />
            </p>
            <p className="mt-1 text-sm text-gray-main">
              <FormattedMessage id="faq.more.desc" />
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link
              href={`${homePath(locale)}#contact`}
              onClick={() => trackCtaClick("article-faq", "contact")}
              className="text-center bg-blue-ulight text-brand-ink font-semibold text-sm px-6 py-3 rounded-full hover:bg-blue-light transition-colors duration-200"
            >
              <FormattedMessage id="faq.more.contact" />
            </Link>
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCtaClick("article-faq", "demo")}
              className="text-center bg-blue-main text-white font-semibold text-sm px-6 py-3 rounded-full hover:bg-blue-fill transition-colors duration-200"
            >
              <FormattedMessage id="faq.more.demo" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
